// PricingPage.tsx
import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Check,
  ChevronDown,
  ChevronUp,
  ArrowRightIcon,
  SparklesIcon,
  Zap,
} from "lucide-react";
import FooterSection from "./FooterSection";
import LoginModal from "./LoginModal";
import SignUpModal from "./SignUpModal";
import Logo from "../assets/KavoaLogo.png";

interface PricingPageProps {
  onOpenSignup: () => void;
  onOpenLogin: () => void;
  onSuccessfulLogin: (data: { token: string; userId: string }) => void;
}

interface FAQ {
  question: string;
  answer: string;
}

const faqs: FAQ[] = [
  {
    question: "Can I switch plans later?",
    answer:
      "Yes, you can upgrade or downgrade at any time. Changes take effect at your next billing cycle.",
  },
  {
    question: "What happens after my trial ends?",
    answer:
      "You'll be moved to the Starter plan features. Your data is safe  -  upgrade anytime to unlock advanced features.",
  },
  {
    question: "Is there a setup fee?",
    answer:
      "No. There are no setup fees, no hidden charges, and no long-term contracts.",
  },
  {
    question: "Do you offer discounts for larger teams?",
    answer:
      "Yes! Contact us for custom pricing for firms with 10+ users.",
  },
];

const tiers = [
  {
    name: "Starter",
    description: "Everything you need to manage your practice",
    monthlyPrice: 39,
    annualPrice: 29,
    popular: false,
    features: [
      "Unlimited Clients & Matters",
      "Calendar & Task Management",
      "Basic Billing & Invoicing",
      "Document Management",
      "Reports & Dashboard",
    ],
    includesLine: null,
  },
  {
    name: "Professional",
    description: "Advanced tools for growing firms",
    monthlyPrice: 69,
    annualPrice: 55,
    popular: true,
    features: [
      "Messaging & Calls (SMS, Email, Voice)",
      "Leads & Intake Pipeline",
      "E-Signatures",
      "Custom Pipeline Stages",
      "Payment Plans & Autopay",
    ],
    includesLine: "Everything in Starter",
  },
  {
    name: "Firm",
    description: "Full-suite for specialized practices",
    monthlyPrice: 99,
    annualPrice: 79,
    popular: false,
    features: [
      "Immigration Case Management",
      "Real Estate Module",
      "Trust Accounting",
      "Custom Branding",
      "Priority Support",
    ],
    includesLine: "Everything in Professional",
  },
];

const PricingPage: React.FC<PricingPageProps> = ({
  onOpenSignup,
  onOpenLogin,
  onSuccessfulLogin,
}) => {
  const [isAnnual, setIsAnnual] = useState(true);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);

  const handleOpenSignup = () => {
    setSignupOpen(true);
    onOpenSignup();
  };

  const handleOpenLogin = () => {
    setLoginOpen(true);
    onOpenLogin();
  };

  return (
    <div className="min-h-screen w-full bg-white">
      {/* Modals */}
      <LoginModal
        isOpen={loginOpen}
        onOpenChange={setLoginOpen}
        onSuccessfulLogin={onSuccessfulLogin}
      />
      <SignUpModal
        isOpen={signupOpen}
        onOpenChange={setSignupOpen}
        onSuccessfulLogin={onSuccessfulLogin}
      />

      {/* Navigation */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-1">
          <div className="flex items-center justify-between">
            <motion.div
              className="flex items-center gap-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <a href="/" className="relative">
                <img
                  src={Logo}
                  alt="Kavoa"
                  className="h-20 rounded-lg object-contain"
                />
              </a>
            </motion.div>

            <motion.div
              className="flex items-center gap-4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <a
                href="/pricing"
                className="px-5 py-2.5 text-sm font-semibold transition-colors"
                style={{ color: "#0F5132" }}
              >
                Pricing
              </a>

              <button
                onClick={handleOpenLogin}
                className="px-5 py-2.5 text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors"
              >
                Sign In
              </button>

              <button
                onClick={handleOpenSignup}
                className="px-6 py-2.5 text-sm font-semibold text-white rounded-xl transition-all shadow-md hover:shadow-lg"
                style={{ backgroundColor: "#0F5132" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#146C43";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#0F5132";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                Get Started
              </button>
            </motion.div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-slate-50 opacity-60" />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 50%, rgba(15, 81, 50, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(25, 135, 84, 0.03) 0%, transparent 50%)",
          }}
        />

        <div className="relative max-w-4xl mx-auto px-6 pt-16 pb-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-emerald-100 shadow-sm mb-8"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <SparklesIcon className="h-4 w-4" style={{ color: "#198754" }} />
              <span
                className="text-sm font-semibold"
                style={{ color: "#0F5132" }}
              >
                No Hidden Fees
              </span>
            </motion.div>

            <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-[1.1] tracking-tight text-slate-800">
              Simple, Transparent{" "}
              <span className="relative inline-block">
                <span className="relative z-10" style={{ color: "#0F5132" }}>
                  Pricing
                </span>
                <motion.span
                  className="absolute bottom-2 left-0 w-full h-3 bg-gradient-to-r from-emerald-200 to-emerald-300 -z-0"
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                />
              </span>
            </h1>

            <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Start with a 14-day free trial. No credit card required. Upgrade,
              downgrade, or cancel anytime.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Billing Toggle */}
      <section className="relative pt-4 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            className="flex items-center justify-center gap-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <div className="inline-flex items-center bg-slate-100 rounded-full p-1">
              <button
                onClick={() => setIsAnnual(false)}
                className={`px-6 py-2.5 text-sm font-semibold rounded-full transition-all ${
                  !isAnnual
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                className={`px-6 py-2.5 text-sm font-semibold rounded-full transition-all ${
                  isAnnual
                    ? "text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
                style={
                  isAnnual
                    ? { backgroundColor: "#0F5132" }
                    : {}
                }
              >
                Annual
              </button>
            </div>
            {isAnnual && (
              <motion.span
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold"
                style={{ backgroundColor: "#E8F5E9", color: "#0F5132" }}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Zap className="h-3 w-3" />
                Save ~25%
              </motion.span>
            )}
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="relative pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {tiers.map((tier, index) => {
              const price = isAnnual ? tier.annualPrice : tier.monthlyPrice;
              return (
                <motion.div
                  key={tier.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15, duration: 0.6 }}
                  className={`relative bg-white rounded-3xl p-8 border ${
                    tier.popular
                      ? "border-2 shadow-xl"
                      : "border-slate-200 shadow-sm hover:shadow-lg"
                  } transition-all duration-300 flex flex-col`}
                  style={
                    tier.popular
                      ? { borderColor: "#0F5132" }
                      : {}
                  }
                >
                  {/* Most Popular Badge */}
                  {tier.popular && (
                    <div
                      className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-xs font-bold text-white"
                      style={{ backgroundColor: "#0F5132" }}
                    >
                      Most Popular
                    </div>
                  )}

                  {/* Tier Header */}
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                      {tier.name}
                    </h3>
                    <p className="text-sm text-slate-500">{tier.description}</p>
                  </div>

                  {/* Price */}
                  <div className="mb-8">
                    <div className="flex items-baseline gap-1">
                      <span className="text-5xl font-bold text-slate-800">
                        ${price}
                      </span>
                      <span className="text-slate-500 text-sm font-medium">
                        /user/mo
                      </span>
                    </div>
                    <p className="text-sm text-slate-400 mt-1">
                      {isAnnual ? "Billed annually" : "Billed monthly"}
                    </p>
                  </div>

                  {/* CTA Button */}
                  {tier.popular ? (
                    <button
                      onClick={handleOpenSignup}
                      className="w-full py-3.5 text-sm font-bold text-white rounded-xl transition-all shadow-md hover:shadow-lg mb-8"
                      style={{ backgroundColor: "#0F5132" }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#146C43";
                        e.currentTarget.style.transform = "translateY(-1px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#0F5132";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      Start Free Trial
                    </button>
                  ) : (
                    <button
                      onClick={handleOpenSignup}
                      className="w-full py-3.5 text-sm font-bold rounded-xl border-2 transition-all hover:shadow-md mb-8"
                      style={{ borderColor: "#0F5132", color: "#0F5132" }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#F0FDF4";
                        e.currentTarget.style.transform = "translateY(-1px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      Start Free Trial
                    </button>
                  )}

                  {/* Features */}
                  <div className="flex-1">
                    <ul className="space-y-3.5">
                      {tier.includesLine && (
                        <li className="flex items-center gap-3">
                          <Check
                            className="h-5 w-5 flex-shrink-0"
                            style={{ color: "#198754" }}
                          />
                          <span className="text-sm font-semibold text-slate-700">
                            {tier.includesLine}
                          </span>
                        </li>
                      )}
                      {tier.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-3">
                          <Check
                            className="h-5 w-5 flex-shrink-0"
                            style={{ color: "#198754" }}
                          />
                          <span className="text-sm text-slate-600">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Trust line */}
          <motion.p
            className="text-center text-sm text-slate-500 mt-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            All plans include a 14-day free trial. No credit card required.
          </motion.p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-slate-800">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600">
              Everything you need to know about our pricing.
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
              >
                <button
                  onClick={() =>
                    setOpenFaq(openFaq === index ? null : index)
                  }
                  className="w-full flex items-center justify-between px-6 py-5 text-left"
                >
                  <span className="text-base font-semibold text-slate-800">
                    {faq.question}
                  </span>
                  {openFaq === index ? (
                    <ChevronUp className="h-5 w-5 text-slate-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>
                <motion.div
                  initial={false}
                  animate={{
                    height: openFaq === index ? "auto" : 0,
                    opacity: openFaq === index ? 1 : 0,
                  }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <p className="px-6 pb-5 text-sm text-slate-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-28 bg-gradient-to-br from-emerald-900 to-emerald-800 relative overflow-hidden">
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-5xl lg:text-6xl font-bold mb-8 text-white leading-tight">
              Ready to Simplify Your Practice?
            </h2>
            <p className="text-xl text-emerald-100 mb-12 max-w-3xl mx-auto leading-relaxed">
              Join law firms already using Kavoa to manage clients, billing,
              documents, and more. Start your 14-day free trial today.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button
                onClick={handleOpenSignup}
                className="group inline-flex items-center justify-center gap-3 px-10 py-5 text-lg font-bold bg-white rounded-xl transition-all shadow-2xl hover:shadow-3xl hover:scale-105"
                style={{ color: "#0F5132" }}
              >
                Start Free Trial
                <ArrowRightIcon className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            <p className="text-sm text-emerald-200 mt-8">
              No credit card required. Cancel anytime.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <FooterSection />
    </div>
  );
};

export default PricingPage;
